/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import app, { db } from './firebase.ts';
import { dataService } from './dataService.ts';
import { NotificationService } from './notificationService.ts';

export interface FCMTokenRecord {
  id: string;
  token: string;
  userId?: string;
  userName: string;
  userRole: string;
  clientId?: string;
  platform: string;
  createdAt: string;
  lastActive: string;
}

export interface CriticalPushPayload {
  title: string;
  body: string;
  folioId?: string;
  clientName?: string;
  newStatus?: string;
  category: 'RETRABAJO' | 'DEVOLUCION' | 'ENTREGA_PLANTA' | 'PREFACTURA' | 'URGENTE' | 'GENERAL';
  url?: string;
}

const FCM_TOKEN_STORAGE_KEY = 'snf_sic_fcm_token';

class FCMNotificationService {
  private messaging: Messaging | null = null;
  private isMessagingSupported: boolean = false;
  private currentToken: string | null = null;

  constructor() {
    this.init();
  }

  getCurrentToken(): string | null {
    if (this.currentToken) return this.currentToken;
    if (typeof window !== 'undefined') {
      return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    }
    return null;
  }

  private async init() {
    if (typeof window === 'undefined') return;

    try {
      const supported = await isSupported();
      this.isMessagingSupported = supported;

      if (supported) {
        this.messaging = getMessaging(app);
        console.log('[FCM] Firebase Cloud Messaging inicializado correctamente.');
        this.setupForegroundListener();
      } else {
        console.warn('[FCM] FCM no es directamente soportado en este entorno browser/iframe.');
      }
    } catch (err) {
      console.warn('[FCM] Error al inicializar Firebase Messaging:', err);
    }
  }

  // Register Service Worker and retrieve FCM Token
  async requestFCMToken(userName: string = 'Usuario Operativo', userRole: string = 'OPERADOR', clientId?: string): Promise<string | null> {
    if (typeof window === 'undefined') return null;

    // Check notification permissions
    if (Notification.permission !== 'granted') {
      try {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          console.warn('[FCM] Permiso de notificaciones denegado por el usuario.');
          return null;
        }
      } catch (e) {
        console.warn('[FCM] Error solicitando permisos de notificación:', e);
      }
    }

    try {
      // Register or obtain FCM service worker
      let swRegistration: ServiceWorkerRegistration | null = null;
      if ('serviceWorker' in navigator) {
        try {
          swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
          await navigator.serviceWorker.ready;
          console.log('[FCM] Service Worker /firebase-messaging-sw.js registrado exitosamente.');
        } catch (swErr) {
          console.warn('[FCM] Error registrando /firebase-messaging-sw.js, intentando /sw.js fallback:', swErr);
          swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
          await navigator.serviceWorker.ready;
        }
      }

      let token: string | null = null;

      if (this.messaging && swRegistration) {
        try {
          token = await getToken(this.messaging, {
            serviceWorkerRegistration: swRegistration
          });
        } catch (tokenErr) {
          console.warn('[FCM] Error al obtener token oficial FCM (VAPID/Quota restriction):', tokenErr);
        }
      }

      // Fallback synthetic device token for persistent offline/sandbox execution
      if (!token) {
        const stored = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
        token = stored || `fcm_dev_token_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      this.currentToken = token;
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);

      // Register/Update token record in Firestore & DataService
      const tokenRecord: FCMTokenRecord = {
        id: `fcm-${token.substring(0, 16)}`,
        token: token,
        userName,
        userRole,
        clientId,
        platform: navigator.userAgent,
        createdAt: new Date().toISOString(),
        lastActive: new Date().toISOString()
      };

      try {
        await dataService.saveDocument('fcm_tokens', tokenRecord);
        console.log('[FCM] Token registrado con éxito en Firestore:', tokenRecord.id);
      } catch (dbErr) {
        console.warn('[FCM] No se pudo persistir el token en Firestore:', dbErr);
      }

      return token;
    } catch (error) {
      console.error('[FCM] Error en requestFCMToken:', error);
      return null;
    }
  }

  // Get current active token
  getTokenFromStorage(): string | null {
    if (typeof window === 'undefined') return null;
    return this.currentToken || localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  }

  // Setup listener for foreground push messages
  private setupForegroundListener() {
    if (!this.messaging) return;

    try {
      onMessage(this.messaging, (payload) => {
        console.log('[FCM Foreground] Mensaje recibido en primer plano:', payload);

        const title = payload.notification?.title || payload.data?.title || '🚨 Alerta de Folio - SNF SIC';
        const body = payload.notification?.body || payload.data?.body || 'Se ha registrado una actualización crítica de estatus.';
        const category = (payload.data?.category as any) || 'GENERAL';

        // 1. Play sound chime
        NotificationService.sendNotification(
          title,
          {
            body,
            icon: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
            tag: payload.data?.tag || `fcm-msg-${Date.now()}`
          },
          category === 'RETRABAJO' || category === 'DEVOLUCION' ? 'critical' : 'info'
        );

        // 2. Dispatch event for UI banner
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('fcm_critical_alert', {
            detail: { title, body, payload }
          });
          window.dispatchEvent(event);
        }
      });
    } catch (e) {
      console.warn('[FCM] Error configurando listener en primer plano:', e);
    }
  }

  // Broadcast critical status notification across device network (Firestore & Push API)
  async sendCriticalStatusNotification(payload: CriticalPushPayload): Promise<boolean> {
    console.log('[FCM Dispatch] Enviando alerta crítica de estado:', payload);

    const notificationId = `NOTIF-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const record = {
      id: notificationId,
      title: payload.title,
      body: payload.body,
      folioId: payload.folioId || '',
      clientName: payload.clientName || 'General',
      category: payload.category,
      newStatus: payload.newStatus || '',
      url: payload.url || '/#trazabilidad',
      timestamp: new Date().toISOString(),
      sentByFCM: true,
      read: false
    };

    // 1. Save to Firestore `push_notifications` collection for global real-time synchronization
    try {
      await dataService.saveDocument('push_notifications', record);
    } catch (err) {
      console.warn('[FCM Dispatch] Error al guardar notificación en Firestore:', err);
    }

    // 2. Trigger native Service Worker Push notification locally & in background
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready;
        if (reg && reg.showNotification) {
          await reg.showNotification(payload.title, {
            body: payload.body,
            icon: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
            badge: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
            vibrate: [300, 100, 300, 100, 500],
            requireInteraction: payload.category === 'RETRABAJO' || payload.category === 'DEVOLUCION',
            tag: `folio-${payload.folioId || 'alert'}-${Date.now()}`,
            data: {
              url: payload.url || '/#trazabilidad',
              folioId: payload.folioId
            },
            actions: [
              { action: 'open', title: '👁️ Ver Folio' },
              { action: 'dismiss', title: '✖️ Entendido' }
            ]
          } as any);
        }
      } catch (swErr) {
        console.warn('[FCM Dispatch] Error al mostrar notificación mediante ServiceWorker:', swErr);
      }
    }

    // 3. Fallback native browser Notification
    NotificationService.sendNotification(
      payload.title,
      { body: payload.body },
      payload.category === 'RETRABAJO' || payload.category === 'DEVOLUCION' ? 'critical' : 'info'
    );

    return true;
  }
}

export const fcmService = new FCMNotificationService();
